import axios from "axios";

const configuredBackend = process.env.REACT_APP_BACKEND_URL?.trim();
const API = configuredBackend ? `${configuredBackend.replace(/\/+$/, "")}/api` : "/api";
export const API_BASE = API;

// Send the httpOnly session cookies with every request.
axios.defaults.withCredentials = true;

function csrfToken() {
  const cookie = document.cookie.split("; ").find((part) => part.startsWith("ov_csrf="));
  return cookie ? decodeURIComponent(cookie.slice("ov_csrf=".length)) : "";
}

axios.interceptors.request.use((config) => {
  if (["post", "put", "patch", "delete"].includes((config.method || "").toLowerCase())) {
    const token = csrfToken();
    if (token) config.headers["X-CSRF-Token"] = token;
  }
  return config;
});

// If the short-lived access token expired, silently refresh it once and retry.
let refreshing = null;
axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { config, response } = error;
    if (
      response?.status === 401 &&
      config &&
      !config._retried &&
      !config.url.includes("/auth/login") &&
      !config.url.includes("/auth/refresh") &&
      !config.url.includes("/auth/me")
    ) {
      config._retried = true;
      refreshing = refreshing || axios.post(`${API}/auth/refresh`).finally(() => { refreshing = null; });
      try {
        await refreshing;
        return axios(config);
      } catch {
        // no valid session — fall through and reject
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authLogin = (email, password) =>
  axios.post(`${API}/auth/login`, { email, password }).then((r) => r.data);
export const authLogout = () => axios.post(`${API}/auth/logout`).then((r) => r.data);
export const authMe = () => axios.get(`${API}/auth/me`).then((r) => r.data);
export const authChangePassword = (current_password, new_password) =>
  axios.post(`${API}/auth/change-password`, { current_password, new_password }).then((r) => r.data);

// Public
export const getPublishedProjects = () =>
  axios.get(`${API}/projects/published`).then((r) => r.data);

export const sendMessage = (data) =>
  axios.post(`${API}/contact`, data).then((r) => r.data);

// Admin (no auth yet — see the warning banner on /admin)
export const adminStats = () => axios.get(`${API}/admin/stats`).then((r) => r.data);

export const adminListProjects = () =>
  axios.get(`${API}/admin/projects`).then((r) => r.data);
export const adminCreateProject = (data) =>
  axios.post(`${API}/admin/projects`, data).then((r) => r.data);
export const adminUpdateProject = (id, data) => {
  const { content_sha, ...payload } = data;
  return axios.put(`${API}/admin/projects/${id}`, payload, { headers: content_sha ? { "If-Match": content_sha } : {} }).then((r) => r.data);
};
export const adminDeleteProject = (id, expectedSha) =>
  axios.delete(`${API}/admin/projects/${id}`, { headers: expectedSha ? { "If-Match": expectedSha } : {} });

export const adminListNotes = () =>
  axios.get(`${API}/admin/notes`).then((r) => r.data);
export const adminCreateNote = (data) =>
  axios.post(`${API}/admin/notes`, data).then((r) => r.data);
export const adminUpdateNote = (id, data) => {
  const { content_sha, ...payload } = data;
  return axios.put(`${API}/admin/notes/${id}`, payload, { headers: content_sha ? { "If-Match": content_sha } : {} }).then((r) => r.data);
};
export const adminDeleteNote = (id, expectedSha) =>
  axios.delete(`${API}/admin/notes/${id}`, { headers: expectedSha ? { "If-Match": expectedSha } : {} });
export const adminImportNote = (file) => {
  const form = new FormData();
  form.append("file", file);
  return axios.post(`${API}/admin/notes/import`, form).then((r) => r.data);
};

export const adminListMessages = () =>
  axios.get(`${API}/admin/messages`).then((r) => r.data);
export const adminMarkMessageRead = (id, read) =>
  axios.put(`${API}/admin/messages/${id}/read`, null, { params: { read } }).then((r) => r.data);
export const adminDeleteMessage = (id) =>
  axios.delete(`${API}/admin/messages/${id}`);

// Site settings (bio, goals, links)
export const VALID_SITE_SHA_PATTERN = /^[0-9a-f]{40}$/i;

export function isValidSiteSha(sha) {
  return typeof sha === "string" && VALID_SITE_SHA_PATTERN.test(sha);
}

export function getSiteSaveReadiness({ site, loading, loadError, expectedSha } = {}) {
  if (loading) {
    return {
      ready: false,
      reason: "loading",
      message: "Site settings are still loading from the server. Please wait before saving.",
    };
  }
  if (loadError) {
    return {
      ready: false,
      reason: "load_error",
      message:
        typeof loadError === "string"
          ? loadError
          : "Failed to load site settings from the server. Retry loading before saving.",
    };
  }
  const sha = expectedSha !== undefined ? expectedSha : site?.content_sha;
  if (!isValidSiteSha(sha)) {
    return {
      ready: false,
      reason: "missing_sha",
      message:
        "Site settings do not have a valid server version SHA (content_sha). Saving is disabled until the server site document is initialized.",
    };
  }
  return {
    ready: true,
    reason: "ready",
    sha,
    message: "",
  };
}

export const getSite = () => axios.get(`${API}/site`).then((r) => r.data);
export const adminUpdateSite = (data) => {
  const { content_sha, ...payload } = data || {};
  if (!isValidSiteSha(content_sha)) {
    const error = new Error(
      "Cannot save site settings before initial server version (content_sha) is loaded."
    );
    error.code = "SITE_NOT_INITIALIZED";
    error.response = {
      status: 422,
      data: {
        detail: "Cannot save site settings before initial server version (content_sha) is loaded.",
        error: {
          code: "SITE_NOT_INITIALIZED",
          message: "Cannot save site settings before initial server version (content_sha) is loaded.",
        },
      },
    };
    return Promise.reject(error);
  }
  return axios
    .put(`${API}/admin/site`, payload, {
      headers: { "If-Match": content_sha },
    })
    .then((r) => r.data);
};
